/**
 * TSPL, as spoken by the Jadens JD-268BT.
 *
 * The printer's firmware understands only the subset of TSPL its Windows
 * driver emits: SIZE / REFERENCE / GAP / OFFSET / SETC / CLS / BITMAP / PRINT.
 * Anything else (TEXT, BOX, BAR, DIRECTION, a status query) jams its parser
 * until someone power-cycles it (established by experiment, 2026-09-13). So
 * the encoder emits a label exactly as the driver frames it, and the guard
 * accepts a job only when every command is on that list. The guard runs on
 * the device immediately before bytes are sent, on every transport.
 */
import type { MonoBitmap } from "./bitmap.ts";

export interface TsplMedia {
  widthIn: number;
  heightIn: number;
  /** Gap between labels in dots; the driver sends 24 (3 mm). */
  gapDots: number;
}

const ascii = (s: string) => new TextEncoder().encode(s);

/** zlib-wrapped deflate, which TSPL's compressed BITMAP mode (3) expects. */
export async function zlibDeflate(data: Uint8Array<ArrayBuffer>): Promise<Uint8Array<ArrayBuffer>> {
  if (typeof CompressionStream === "undefined") {
    throw new Error("This browser is too old to print labels directly. Update it and try again.");
  }
  const stream = new Blob([data]).stream().pipeThrough(new CompressionStream("deflate"));
  return new Uint8Array(await new Response(stream).arrayBuffer());
}

export async function encodeTspl(bitmap: MonoBitmap, media: TsplMedia, copies = 1): Promise<Uint8Array<ArrayBuffer>> {
  if (!Number.isInteger(copies) || copies < 1 || copies > 99) throw new Error("copies must be 1 to 99");
  const image = await zlibDeflate(bitmap.bits);
  const mm = (inches: number) => (inches * 25.4).toFixed(2);
  const head = ascii(
    [
      `SIZE ${mm(media.widthIn)} mm,${mm(media.heightIn)} mm`,
      "REFERENCE 0,0",
      `GAP ${media.gapDots} dot,0 dot`,
      "OFFSET 0 dot",
      "SETC AUTODOTTED OFF",
      "SETC PAUSEKEY ON",
      "SETC WATERMARK OFF",
      "CLS",
      `BITMAP 0,0,${bitmap.widthBytes},${bitmap.height},3,${image.length},`,
    ].join("\r\n")
  );
  const tail = ascii(`\r\nPRINT ${copies},1\r\n`);
  const job = new Uint8Array(head.length + image.length + tail.length);
  job.set(head, 0);
  job.set(image, head.length);
  job.set(tail, head.length + image.length);
  return job;
}

// Only what the driver was seen to send. DENSITY/SPEED exist in the driver but
// were never proven on the hardware, so they stay off the list until they are.
const HEADER_COMMANDS = ["SIZE ", "REFERENCE ", "GAP ", "OFFSET ", "SETC ", "CLS"];
export const MAX_TSPL_JOB_BYTES = 300_000;

/** Returns a reason the job is unsafe for the printer, or null when it is fine. */
export function checkTspl(job: Uint8Array): string | null {
  if (job.length > MAX_TSPL_JOB_BYTES) return `job is ${job.length} bytes; the limit is ${MAX_TSPL_JOB_BYTES}`;
  const latin1 = (start: number, end: number) => {
    let s = "";
    for (let i = start; i < end; i++) s += String.fromCharCode(job[i]);
    return s;
  };
  const bitmapAt = indexOf(job, "BITMAP ");
  if (bitmapAt < 0) return "no BITMAP command; the printer only prints bitmaps";

  for (const line of latin1(0, bitmapAt).split("\r\n")) {
    if (line === "") continue;
    if (!HEADER_COMMANDS.some((c) => (c.endsWith(" ") ? line.startsWith(c) : line === c))) {
      return `command "${line.slice(0, 20)}" is not one the printer accepts`;
    }
  }

  // BITMAP x,y,widthBytes,height,mode,[length,]data
  let cursor = bitmapAt + "BITMAP ".length;
  const fields: number[] = [];
  while (fields.length < 5) {
    const comma = indexOf(job, ",", cursor);
    if (comma < 0) return "BITMAP header is cut short";
    const text = latin1(cursor, comma);
    const n = Number(text);
    if (!/^\d+$/.test(text) || !Number.isSafeInteger(n)) return "BITMAP header has a bad number";
    fields.push(n);
    cursor = comma + 1;
  }
  const [x, y, widthBytes, height, mode] = fields;
  if (x !== 0 || y !== 0) return "bitmap must start at 0,0";
  if (widthBytes < 1 || widthBytes > 128 || height < 1 || height > 2400) return "bitmap size is out of range";
  let dataLength: number;
  if (mode === 3) {
    const comma = indexOf(job, ",", cursor);
    if (comma < 0) return "BITMAP header is cut short";
    const text = latin1(cursor, comma);
    dataLength = Number(text);
    if (!/^\d+$/.test(text) || dataLength < 2) return "compressed bitmap length is bad";
    cursor = comma + 1;
    if (job[cursor] !== 0x78) return "compressed bitmap is not zlib data";
  } else if (mode === 0) {
    dataLength = widthBytes * height;
  } else {
    return `bitmap mode ${mode} is not one the printer accepts`;
  }
  if (cursor + dataLength > job.length) return "bitmap data is cut short";
  const tail = latin1(cursor + dataLength, job.length);
  if (!/^\r\nPRINT [1-9]\d?(,[1-9]\d?)?\r\n$/.test(tail)) {
    return "job must end with a single PRINT right after the bitmap";
  }
  return null;
}

function indexOf(hay: Uint8Array, needle: string, from = 0): number {
  const n = Array.from(needle, (ch) => ch.charCodeAt(0));
  outer: for (let i = from; i <= hay.length - n.length; i++) {
    for (let j = 0; j < n.length; j++) if (hay[i + j] !== n[j]) continue outer;
    return i;
  }
  return -1;
}
